import { Injectable } from '@angular/core';
import { SwUpdate } from '@angular/service-worker';
import { Router } from '@angular/router';
import { Subscription, interval } from 'rxjs';
// services
// import { SurveyService } from './survey.service';
import { NetworkService } from './network.service';

@Injectable({
  providedIn: 'root'
})
export class UpdateWorkerService {

  public updateNeeded : boolean = false;
  ngswConfig          : any = null;
  auto_update_sub     : Subscription;
  check_cache_sub     : Subscription;
  private pendingVersionReady: any = null;
  private readonly skipPendingModalKey = 'skip_pending_modal_once';
  private readonly showUpdateSnackKey = 'show_update_snack_once';

  constructor(
    private swUpdate      : SwUpdate,
    // private surveyService : SurveyService,
    private router        : Router,
    private network       : NetworkService 
    ) {

    this.getNgswConfig();
    // console.log('init swUpdate service');
    if (!this.swUpdate.isEnabled) {
      // console.log('update service not available');
      return;
    } else {
      // Initial activation for first-time users
      if (!localStorage.getItem('sc')) {
        this.swUpdate.activateUpdate().then(() => {
          localStorage.setItem('sc', '1');
        });
      }

      this.checkUpdateAvailible();
      swUpdate.checkForUpdate();

      // Optimized cache checking - less frequent and more efficient
      this.check_cache_sub = interval(5000).subscribe( async () => {
        const file_cached = await this.areResourcesCached();
        if (file_cached && this.network.status) { 
          this.network.offline_mode_available.next(true);
          this.check_cache_sub.unsubscribe();
        }
      })

      // Optimized update checking - every 2 hours instead of 1 hour
      interval(120 * 60 * 1000).subscribe(() => {
        swUpdate.checkForUpdate();
      });
    }

  }
  /**
   * Automatically checks for available updates and applies them when ready.
   *
   * This function subscribes to version update events from the Service Worker (`swUpdate`).
   * When an update is detected, it automatically downloads, prepares for activation, and then
   * applies the update by refreshing the window. 
   * 
   * - When a new version is detected, it logs the hash of the new version being downloaded.
   * - Once the new version is ready to be used, it logs both the current and the latest version hashes, 
   *   activates the update, and reloads the window to apply the changes.
   * - If there's any failure in installing the update, it logs an error message with the version hash.
   * @Date 24-08-2023
   * @Author Daniel García
   */
  checkUpdateAvailible() {
    this.auto_update_sub = this.swUpdate.versionUpdates.subscribe( (evt) => {
      // console.log("evt 4", evt);
      switch (evt.type) {
        case 'VERSION_DETECTED':
          // console.log(`Downloading new app version: ${evt.version.hash}`);
          break;
        case 'VERSION_READY':
          // console.log(`Current app version: ${evt.currentVersion.hash}`);
          // console.log(`New app version ready for use: ${evt.latestVersion.hash}`);
          this.handleVersionReady(evt);
          break;
        case 'VERSION_INSTALLATION_FAILED':
          // console.log(`Failed to install app version '${evt.version.hash}': ${evt.error}`);
          break;
      }
    });
  }
  /**
   * Loads the new application version automatically.
   *
   * The function activates the Service Worker update and reloads the window to apply the new version.
   * Only avoids updating on the 'thanks' page to prevent interrupting survey completion.
   * 
   * @author Daniel García
   * @date 25-08-2023
   */
  loadNewVersion() {
    if (!this.router.url.includes('thanks')) {
      this.setSkipPendingModalFlags();
      this.swUpdate.activateUpdate().then(() => window.location.reload());
    }
  }
  /**
   * Persist short-lived flags to drive UX after reload.
   */
  private setSkipPendingModalFlags(): void {
    try {
      localStorage.setItem(this.skipPendingModalKey, '1');
      localStorage.setItem(this.showUpdateSnackKey, '1');
    } catch (error) {
      console.warn('Failed to persist update flags before reload:', error);
    }
  }
  /**
   * Handle VERSION_READY event to avoid interrupting the thanks page.
   */
  private handleVersionReady(evt: any): void {
    if (this.router.url.includes('thanks')) {
      this.pendingVersionReady = evt;
      return;
    }
    this.pendingVersionReady = evt;
    this.loadNewVersion();
  }
  /**
   * Apply an update that was deferred (e.g. because user was on thanks page).
   */
  applyPendingUpdateIfAny(): void {
    if (!this.pendingVersionReady) return;
    this.loadNewVersion();
    this.pendingVersionReady = null;
  }
  /**
   * Get service worker configuration
   * @Date 24-08-2023
   * @Author Daniel García
   */
  async getNgswConfig() {
    this.ngswConfig = [
      {
        "name": "app",
        "installMode": "prefetch",
        "updateMode": "prefetch",
        "resources": {
          "files": [
            "/favicon.ico",
            "/index.html",
            "/manifest.webmanifest",
            "/*.css",
            "/*.js"
          ]
        },
        "cacheQueryOptions": {"ignoreSearch": true }
      }
    ]
  }
  /**
   * Checks if resources are cached in the Service Worker's asset cache.
   *
   * This function retrieves all cache keys, then looks for the specific application cache
   * key that starts with 'ngsw:/:', and ends with ':assets:app:cache'. If the App cache 
   * key is found, it fetches the cached responses within and maps them to their respective URLs.
   *
   * The function returns `true` if the number of cached URLs exceeds 11, otherwise, it returns `false`.
   *
   * @async
   * @returns {Promise<boolean>} A promise that resolves to `true` if the number of cached URLs is more than 11, otherwise `false`.
   * @author Daniel García
   * @date 25-08-2023
   */
  async areResourcesCached(): Promise<boolean> {
    // Get all cache keys
    const cacheKeys = await caches.keys();
    // Find App cache
    const appCacheKey = cacheKeys.find(key => key.startsWith('ngsw:/:') && key.endsWith(':assets:app:cache'))
    if (!appCacheKey) {
      return false;
    }
    const cache = await caches.open(appCacheKey);
    const cachedResponses = await cache.keys();
    const cachedURLs = cachedResponses.map(response => response.url);

    return cachedURLs.length > 11;
  }
}

